import type { LearningPath, PathLevel } from '../evaluator/types.ts';
import type { ManifestEntry } from '../content/load.ts';

/**
 * H-133 — progresso por nível e por percurso, derivado (ADR 0019 decisão 5).
 *
 * A fonte única é a lição concluída. Tudo aqui é contagem sobre o manifesto e
 * sobre o conjunto de lições concluídas — **nenhuma percentagem se guarda**,
 * nem aqui nem em coluna nenhuma. É a mesma escolha de `points_events`:
 * guardar a origem e derivar o total, para se poder recalcular sem confiar
 * em nada que possa ficar para trás quando o conteúdo crescer.
 *
 * Uma lição que pertença a um curso e a dois níveis conta nos três a partir
 * da MESMA entrada no conjunto: é a propriedade que justifica a arquitectura
 * (quem fez a trilha de JavaScript vê o Nível II já parcialmente feito, e não
 * `0/70` numa coisa em que já fez metade).
 *
 * **O projecto integrador conta para o nível desde E12 (H-126, RN-011):** na
 * barra, vale `pesoProjecto` lições-equivalentes (o único parâmetro
 * configurável, vindo da economia publicada); na conclusão, é uma condição
 * que não se configura — um nível com todas as lições feitas e o projecto por
 * aprovar está **em curso**, não concluído. Um nível sem projecto declarado
 * continua a ser só a contagem de lições, e a fórmula é a mesma.
 */

export interface Progresso {
  concluidas: number;
  total: number;
}

/** O estado da última entrega de cada projecto, por slug do projecto. */
export type EstadoDoProjecto = 'submetido' | 'em_revisao' | 'aprovado' | 'revisao_necessaria';
export type Projectos = ReadonlyMap<string, EstadoDoProjecto>;

const SEM_PROJECTOS: Projectos = new Map();

/** RN-011 — o projecto conta como feito quando, e só quando, está aprovado. */
export function projectoAprovado(nivel: PathLevel, projectos: Projectos): boolean {
  return nivel.project !== undefined && projectos.get(nivel.project.id) === 'aprovado';
}

/** As lições de um nível, pela ordem dos módulos que ele declara. */
export function licoesDoNivel(nivel: PathLevel, modulos: ManifestEntry[]): string[] {
  const porId = new Map(modulos.map((m) => [m.id, m]));
  return nivel.modules.flatMap((id) => porId.get(id)?.lessons ?? []);
}

export function progressoDoNivel(
  nivel: PathLevel,
  modulos: ManifestEntry[],
  concluidas: ReadonlySet<string>,
  projectos: Projectos = SEM_PROJECTOS,
  pesoProjecto = 0,
): Progresso {
  const licoes = licoesDoNivel(nivel, modulos);
  const peso = nivel.project ? Math.max(0, pesoProjecto) : 0;
  return {
    concluidas:
      licoes.filter((id) => concluidas.has(id)).length +
      (projectoAprovado(nivel, projectos) ? peso : 0),
    total: licoes.length + peso,
  };
}

/**
 * RN-011 — concluído é: todas as lições feitas **e**, se o nível tem projecto,
 * o projecto aprovado. A segunda condição não é configurável; o que se
 * configura é quanto o projecto pesa na barra (`pesoProjecto`), e isso não
 * entra aqui de propósito.
 */
export function nivelConcluido(
  nivel: PathLevel,
  modulos: ManifestEntry[],
  concluidas: ReadonlySet<string>,
  projectos: Projectos = SEM_PROJECTOS,
): boolean {
  const licoes = licoesDoNivel(nivel, modulos);
  if (licoes.length === 0) return false;
  if (!licoes.every((id) => concluidas.has(id))) return false;
  return nivel.project === undefined || projectoAprovado(nivel, projectos);
}

/**
 * A média ponderada dos níveis pelo número de lições — que é, por definição,
 * a soma das concluídas sobre a soma dos totais. Escreve-se como soma para a
 * aritmética não ter um passo intermédio onde arredondar.
 */
export function progressoDoPercurso(
  percurso: LearningPath,
  modulos: ManifestEntry[],
  concluidas: ReadonlySet<string>,
  projectos: Projectos = SEM_PROJECTOS,
  pesoProjecto = 0,
): Progresso {
  return percurso.levels.reduce(
    (acc, nivel) => {
      const p = progressoDoNivel(nivel, modulos, concluidas, projectos, pesoProjecto);
      return { concluidas: acc.concluidas + p.concluidas, total: acc.total + p.total };
    },
    { concluidas: 0, total: 0 },
  );
}

/**
 * H-136 — um nível está aberto quando o anterior está completo. O primeiro
 * está sempre aberto.
 *
 * «Aberto» aqui segue a decisão 6 do ADR 0015, reavaliada para níveis e
 * mantida: **avisa-se, não se impede**. Um nível por abrir aparece como tal e
 * diz o que falta — mas quem quiser entrar numa lição dele entra, porque o
 * desbloqueio fino continua a ser o das lições (RF-014), que já existe. Um
 * cartão bloqueado sem saída é o beco que a casa não deixa existir.
 */
export function nivelAberto(
  percurso: LearningPath,
  nivel: PathLevel,
  modulos: ManifestEntry[],
  concluidas: ReadonlySet<string>,
  projectos: Projectos = SEM_PROJECTOS,
): { aberto: boolean; anterior: PathLevel | null } {
  const ordenados = [...percurso.levels].sort((a, b) => a.order_index - b.order_index);
  const posicao = ordenados.findIndex((n) => n.id === nivel.id);
  if (posicao <= 0) return { aberto: true, anterior: null };

  const anterior = ordenados[posicao - 1]!;
  return { aberto: nivelConcluido(anterior, modulos, concluidas, projectos), anterior };
}
